/**
 * The policy text, written once and rendered by both the app and the public site.
 *
 * Two places showing different versions of the same promise is how a disclosure stops matching
 * behaviour, and matching behaviour is precisely what store review checks. So this is data, and
 * both surfaces render it rather than restating it.
 *
 * Every claim here is meant to be true of the system as built. Where the system does something
 * people would not expect — media backups that never delete, accounting records that survive
 * deletion — it says so plainly rather than omitting it. A policy that is quietly wrong is worse
 * than one that is uncomfortably honest.
 *
 * It has not been reviewed by anyone qualified. That review is a release requirement, not a
 * formality, and nothing here should be taken as having satisfied it.
 */

export const OPERATOR = {
  legalName: 'Tripled A',
  registrationNumber: '1928864',
  country: 'Tunisia',
  supportEmail: 'support@claptal.com',
} as const;

export interface PolicySection {
  heading: string;
  paragraphs: string[];
}

export const CONTENT_RULES: PolicySection = {
  heading: 'Content rules',
  paragraphs: [
    'CLAPTAL is for adults aged 18 and over. Casts are fictional adults.',
    'Do not impersonate real people, use material you have no rights to, or create exploitation, threats, harassment, hate, or unlawful content. These rules are why generated stories are about invented people; it is also what keeps a film about a character from being personal data about a person.',
    'Episodes and comments that are published are reviewed, and anything breaking these rules is removed. Accepting an episode yourself does not bypass that review.',
    'The AI provider applies its own safety filters as well as ours. It sometimes refuses ordinary material, and when it does, the refusal comes from the provider rather than from CLAPTAL.',
  ],
};

export const OWNERSHIP: PolicySection = {
  heading: 'Who owns what is made',
  paragraphs: [
    'Content generated through CLAPTAL — episodes, scripts, reference images, characters and casts — belongs to ' +
      `${OPERATOR.legalName}, not to the account that requested it.`,
    'You keep the ideas and instructions you write. You are granting CLAPTAL what it needs to generate, store, review and publish the results.',
    'This is why deleting an account does not delete the films made in it: they are not yours to erase, and they are not about you. Your personal information is a separate matter and is covered below.',
  ],
};

export const WHAT_IS_STORED: PolicySection = {
  heading: 'What is stored about you',
  paragraphs: [
    'Your email address, your handle and display name, the country you selected, and your confirmation that you are an adult and accept these rules.',
    'What you asked for and what it cost: commands, jobs, generation attempts, the models used and the amounts spent against your allowance.',
    'What you do in the app: series you follow, likes, comments, blocks, saved items, resume positions, and notification preferences.',
    'Drafts, scripts, reference images and rejected generations stay private to you until you publish. Publishing shares the accepted episode, the series information and your creator credit.',
    'We use analytics to understand how the app is used and to find what is broken. You can turn it off at any time under You, with the "Share usage data" switch.',
    'Nothing here is sold, and nothing is shared for advertising.',
  ],
};

export const WHERE_IT_LIVES: PolicySection = {
  heading: 'Where it is held, and who else sees it',
  paragraphs: [
    'The database is hosted in the United States. Media is held in Cloudflare R2. CLAPTAL is available worldwide, so information about people outside the United States is transferred there to be stored.',
    'Five processors handle data on our behalf: Convex for the database, Cloudflare for media storage and delivery, Google for AI generation, Resend for account email, and PostHog for analytics, crash reports, session recordings and generation records. PostHog also holds your information in the United States. Each is bound by a data processing agreement incorporating Standard Contractual Clauses, which is the basis on which the transfer is made.',
    `${OPERATOR.legalName} is registered in ${OPERATOR.country}, outside the European Economic Area. If you are in the EEA or the United Kingdom, your data is therefore handled by a company outside it, using infrastructure in the United States.`,
  ],
};

export const DELETION: PolicySection = {
  heading: 'Deleting your account',
  paragraphs: [
    'You can delete your account in the app, under You, or on the website. Access ends immediately: every session is expired and your allowance is deactivated the moment you confirm.',
    `If you cannot sign in, email ${OPERATOR.supportEmail} and we will do it for you.`,
    'Your profile, your private creative work, your comments, follows, likes and viewing history are then removed. If a paid generation is still running, deletion waits for it to finish rather than abandoning a charge nobody can reconcile.',
    'Two things are deliberately kept. Records of what was charged — the amount, the model and the date, with your instructions and configuration scrubbed — because that is our accounting rather than your content. And what staff did about any report, because a moderation record that disappears with the account it concerns is not a record. Both are restricted to staff.',
    'Database backups are kept for at most 30 days and then deleted, so a copy of your information can persist in a backup for up to a month after you delete your account.',
    'Media backups are kept indefinitely. They hold generated films, which belong to CLAPTAL rather than to you, and the backup exists so that an accidental deletion cannot destroy work permanently.',
    'Your analytics records are deleted too. We queue analytics erasure when you confirm and send the request to our analytics processor after outstanding production is reconciled. The processor handles it in the background rather than instantly. They are not included in the export you can download from the app, so email us if you want them. A minimal account identifier and deletion status are retained to retry analytics erasure and prevent late telemetry from recreating deleted records.',
  ],
};

export const CONTACT: PolicySection = {
  heading: 'Who operates CLAPTAL',
  paragraphs: [
    `CLAPTAL is operated by ${OPERATOR.legalName}, registered in ${OPERATOR.country} under number ${OPERATOR.registrationNumber}.`,
    `For anything at all, including privacy requests and account deletion, write to ${OPERATOR.supportEmail}.`,
  ],
};

export const POLICY_SECTIONS: PolicySection[] = [
  CONTACT,
  CONTENT_RULES,
  OWNERSHIP,
  WHAT_IS_STORED,
  WHERE_IT_LIVES,
  DELETION,
];

/**
 * The genres a series may be given.
 *
 * It was a free text field, which meant the value went straight into the prompt that writes the
 * pitch. Someone typing "adult" — or anything else — would have been steering generation with a
 * word nobody had agreed to, and the content rules then have to argue with the thing that produced
 * it. A closed list is the cheaper place to settle that.
 *
 * Kept short on purpose. A list long enough to cover everything is a list nobody reads.
 */
export const GENRES = [
  'Drama',
  'Romance',
  'Mystery',
  'Thriller',
  'Comedy',
  'Family',
  'Crime',
  'Historical',
  'Science Fiction',
  'Fantasy',
  'Coming of Age',
  'Workplace',
] as const;

export type Genre = (typeof GENRES)[number];
export const isGenre = (value: string): value is Genre =>
  (GENRES as readonly string[]).includes(value);
